/**
 * The coverage assessment's figures and verdict: self-funding capacity, existing policy coverage (when
 * policies count), estimated LTC cost, total coverage, and either the gap or the "well-positioned"
 * message. Renders only a ready assessment, so every figure is a real calculated value; a client with
 * no complete accounts reads "No self-funding accounts added" rather than `$0`.
 */
import { Card, CardContent, CardDescription, CardTitle } from '@design-system';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';

import type { FundingAssessment } from '../lib/derive-funding-assessment';

interface CoverageAssessmentResultsProps {
  assessment: FundingAssessment;
  /** Whether to ask about finding insurance under the gap (hidden when policy steps are skipped). */
  showInsurancePrompt: boolean;
  /** Whether a covered client sees the "well-positioned" message; a gap always shows. */
  showWellPositioned?: boolean;
  /** Fill the host's width instead of a centred column. */
  fullWidth?: boolean;
}

export function CoverageAssessmentResults({
  assessment,
  showInsurancePrompt,
  showWellPositioned = true,
  fullWidth = false,
}: CoverageAssessmentResultsProps) {
  const { yearsTillLtc, totalLtcCost, selfFundingCoverage, policyCoverage, combinedCoverage, gap } =
    assessment;
  const showExistingPolicyCard = policyCoverage !== null && policyCoverage > 0;
  // Names only what actually adds coverage: D2C always counts recorded policies, so a consumer
  // without any has a policy figure of 0 and must not read "+ existing policy".
  const coverageSources = [
    selfFundingCoverage !== null && selfFundingCoverage > 0 && 'self-funding',
    showExistingPolicyCard && 'existing policy',
  ]
    .filter(Boolean)
    .join(' + ');

  return (
    <div className={fullWidth ? undefined : 'mx-auto max-w-3xl'}>
      {/* Metric Cards */}
      <div
        className={`mb-6 grid gap-4 md:grid-cols-2 ${showExistingPolicyCard ? 'lg:grid-cols-3' : ''}`}
      >
        <Card className="gap-2 p-6">
          <CardTitle className="text-sm font-normal text-gray-700">Self-Funding Capacity</CardTitle>
          {selfFundingCoverage === null ? (
            <CardDescription className="text-base font-medium text-gray-700">
              No self-funding accounts added
            </CardDescription>
          ) : (
            <>
              <CardDescription className="text-primary font-sans text-3xl font-bold">
                <LtcCostDisplay value={selfFundingCoverage} />
              </CardDescription>
              <CardDescription className="text-sm text-gray-700">
                {yearsTillLtc}-year projection based on your assets
              </CardDescription>
            </>
          )}
        </Card>
        {showExistingPolicyCard && (
          <Card className="gap-2 p-6">
            <CardTitle className="text-sm font-normal text-gray-700">
              Existing Policy Coverage
            </CardTitle>
            <CardDescription className="text-primary font-sans text-3xl font-bold">
              <LtcCostDisplay value={policyCoverage} />
            </CardDescription>
            <CardDescription className="text-sm text-gray-700">
              Projected coverage from existing LTC policy
            </CardDescription>
          </Card>
        )}
        <Card className="gap-2 p-6">
          <CardTitle className="text-sm font-normal text-gray-700">Estimated LTC Cost</CardTitle>
          <CardDescription className="font-sans text-3xl font-bold text-gray-900">
            <LtcCostDisplay value={totalLtcCost} />
          </CardDescription>
          <CardDescription className="text-sm text-gray-700">
            Average long-term care expenses
          </CardDescription>
        </Card>
      </div>
      {combinedCoverage > 0 && (
        <p className="mb-4 text-center text-sm text-gray-600">
          Total coverage: <LtcCostDisplay value={combinedCoverage} />
          {coverageSources && ` (${coverageSources})`}
        </p>
      )}

      {/* Result Message Card */}
      {(gap > 0 || showWellPositioned) && (
        <Card className="gap-2 border-gray-200 bg-white p-6 py-8 text-center">
          <CardContent className="p-0">
            {gap > 0 ? (
              <>
                <p className="mb-2 text-gray-700">You've got a gap of</p>
                <p className="mb-4 font-sans text-4xl font-bold text-[#F58A07]">
                  <LtcCostDisplay value={gap} />
                </p>
                {showInsurancePrompt && (
                  <p className="text-gray-700">
                    Do you want us to find insurance to help you close that?
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="mb-2 text-lg font-medium text-gray-900">
                  Great news! You're well-positioned
                </p>
                <p className="text-gray-900">
                  We can look for financial vehicles that may be more efficient in a long-term care
                  event.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
